import { z } from 'zod/v4';
import type { ChipProps } from '~/components/granular/chip/Chip.types';
import { createValidatedStorage, useValidatedStorage } from '~/global-state/validated-storage';

/* -------------------------------------------------------------------------------------------------
 * Grid appearance preferences
 *
 * Device-level display preferences for AG Grid tables, controlled from the Appearance section of the
 * Settings page. They are applied as `data-grid-density` / `data-grid-font-size` attributes on the
 * <html> element (see root.tsx), which CSS rules in app/components/data-grid/styles.css translate
 * into `--ag-spacing` / `--ag-font-size` overrides picked up by every grid in the app.
 * -----------------------------------------------------------------------------------------------*/

export const gridDensityEnum = z.enum(['compact', 'standard', 'comfortable']);
export type GridDensity = z.infer<typeof gridDensityEnum>;

export const gridFontSizeEnum = z.enum(['small', 'medium', 'large']);
export type GridFontSize = z.infer<typeof gridFontSizeEnum>;

export const DEFAULT_GRID_DENSITY: GridDensity = gridDensityEnum.enum.standard;
export const DEFAULT_GRID_FONT_SIZE: GridFontSize = gridFontSizeEnum.enum.medium;

/** Chip size used in grid cells when font size is Small — keeps chips proportional to compact text. */
export const GRID_CHIP_SIZE_WHEN_SMALL_FONT = 'x-small' satisfies ChipProps['size'];
export const GRID_CHIP_SIZE_DEFAULT = 'small' satisfies ChipProps['size'];
export const GRID_CHIP_ICON_SIZE_WHEN_SMALL_FONT = 12;
export const GRID_CHIP_ICON_SIZE_DEFAULT = 16;

const densityStorage = createValidatedStorage('local', gridDensityEnum);
const fontSizeStorage = createValidatedStorage('local', gridFontSizeEnum);

export function useGridDensity() {
  return useValidatedStorage(densityStorage, 'gridAppearance.density', DEFAULT_GRID_DENSITY);
}

export function useGridFontSize() {
  return useValidatedStorage(fontSizeStorage, 'gridAppearance.fontSize', DEFAULT_GRID_FONT_SIZE);
}

/** Chip size for AG Grid cell renderers. Non-grid Chip usages should keep an explicit size. */
export function useGridChipSize(): NonNullable<ChipProps['size']> {
  const [fontSize] = useGridFontSize();
  return fontSize === gridFontSizeEnum.enum.small ? GRID_CHIP_SIZE_WHEN_SMALL_FONT : GRID_CHIP_SIZE_DEFAULT;
}

/** Icon pixel size paired with {@link useGridChipSize} for icons inside grid chips. */
export function useGridChipIconSize(): number {
  const [fontSize] = useGridFontSize();
  return fontSize === gridFontSizeEnum.enum.small ? GRID_CHIP_ICON_SIZE_WHEN_SMALL_FONT : GRID_CHIP_ICON_SIZE_DEFAULT;
}
